import { useMemo, useState } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { C } from "../../shared/theme";
import { pageColumn } from "../../shared/layout";
import { BottomNav } from "../../shared/BottomNav";
import { TopBar } from "../../shared/TopBar";
import { HELP } from "../../shared/helpContent";
import { Segmented } from "../../shared/Segmented";
import { Sortable, DragHandle, useDragSensors } from "../../shared/Sortable";
import { intro } from "../../shared/textStyles";
import { TASK_VIEWS } from "./goalOrder";
import { allTasks, deadlineTasks, oldestTasks, tasksByCategory, doneTasks, daysOnList, moveCategory } from "./taskViews";
import { usePhaseAdd } from "../today/usePhaseAdd";
import { TaskListRow } from "./TaskListRow";
import { useTaskLinksOf } from "./useTaskRows";
import { useTickTasks } from "./useTickTasks";

const VIEW_OPTIONS = [[TASK_VIEWS.ALL, "All"], [TASK_VIEWS.DEADLINES, "Deadlines"], [TASK_VIEWS.CATEGORY, "Category"], [TASK_VIEWS.OLDEST, "Oldest"]];
const VIEW_INTRO = {
  [TASK_VIEWS.ALL]: "Every task you haven't done, from A to Z.",
  [TASK_VIEWS.DEADLINES]: "Every task you haven't done, with the soonest deadline first. Tasks with no deadline come last.",
  [TASK_VIEWS.CATEGORY]: "Your tasks grouped by category. Tap a category to show its tasks. Drag ⠿ to move a category.",
  [TASK_VIEWS.OLDEST]: "The tasks that have waited longest, once they've been on your list for a week or more.",
};
const groupLabel = () => ({ fontSize: 13, fontWeight: 700, color: C.textMid, letterSpacing: "0.05em", textTransform: "uppercase" });

// Tasks (the bottom bar, or Home → Tasks): every unfinished task, listed four ways, and the finished ones folded at the end.
// Split from Goals & Tasks on 21 Oct 2026 (Craig: "It's time to extract the Tasks views out to a new Tasks
// page"). The task editor lives in App.jsx, so onOpenTask(task) opens it and onDone(task) ticks a task off.
// `view` and onView(view) remember the chosen view; onMoveCategories(categories) saves a new category order.
// saveTask(taskId, fields) and `phase` (the part of the day it is now) put a task in today.
export function TasksPage({ tasks, goals, categories = [], view, onView, onDone, onOpenTask, onMoveCategories, saveTask, phase, onNav }) {
  const phaseAdd = usePhaseAdd(saveTask, phase);
  const { isTicked, tick } = useTickTasks(onDone);
  const [openCats, setOpenCats] = useState(() => new Set());
  const [doneOpen, setDoneOpen] = useState(false);
  const sensors = useDragSensors();
  const goalById = useMemo(() => new Map((goals || []).map(g => [g.id, g])), [goals]);
  const linksOf = useTaskLinksOf();
  const finished = useMemo(() => doneTasks(tasks), [tasks]);
  const hasOpen = (tasks || []).some(t => !t.done);

  const row = (task, note) => (
    <TaskListRow key={task.id} task={task} goal={goalById.get(task.parentGoalId) || null} links={linksOf(task)} note={note}
      done={isTicked(task.id)} onDone={tick} onOpen={onOpenTask} onAddToday={phaseAdd.addNow} />
  );

  const list = () => {
    if (view === TASK_VIEWS.ALL) return allTasks(tasks).map(t => row(t));
    if (view === TASK_VIEWS.DEADLINES) return deadlineTasks(tasks, goals).map(t => row(t));
    if (view === TASK_VIEWS.OLDEST) {
      const oldest = oldestTasks(tasks);
      if (!oldest.length) return <p style={intro()}>No task has been on your list for a week or more.</p>;
      return oldest.map(t => row(t, { text: `On your list for ${daysOnList(t)} days`, color: C.red }));
    }
    return <CategoryGroups groups={tasksByCategory(tasks, goals, categories)} categories={categories} sensors={sensors}
      openCats={openCats} setOpenCats={setOpenCats} row={row} onMoveCategories={onMoveCategories} />;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg, color: C.text }}>
      <TopBar title="Tasks" help={HELP.tasks} />
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ ...pageColumn, padding: "12px 20px 40px" }}>
          {hasOpen ? (
            <>
              <Segmented label="List tasks by" stretch options={VIEW_OPTIONS} value={view} onChange={onView} />
              <p style={{ ...intro(), margin: "14px 0 6px" }}>{VIEW_INTRO[view]}</p>
              {list()}
            </>
          ) : (
            <p style={intro()}>You don't have any tasks to do. Tap + at the top to add one.</p>
          )}
          {finished.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <button onClick={() => setDoneOpen(o => !o)} aria-expanded={doneOpen}
                style={{ width: "100%", minHeight: 44, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", background: "none", border: "none", borderTop: `1px solid ${C.border}`, cursor: "pointer", fontFamily: "inherit" }}>
                <span style={groupLabel()}>Completed ({finished.length})</span>
                <span aria-hidden="true" style={{ color: C.textMid, fontSize: 11 }}>{doneOpen ? "▲" : "▼"}</span>
              </button>
              {doneOpen && (
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {/* A finished task opens in the editor, where it can be changed or marked not done. */}
                  {finished.map(t => (
                    <li key={t.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <button onClick={() => onOpenTask(t)}
                        style={{ width: "100%", minHeight: 44, padding: "10px 0", background: "none", border: "none", textAlign: "left", cursor: "pointer", fontFamily: "inherit", fontSize: 15, lineHeight: 1.35, color: C.textMid, textDecoration: "line-through" }}>
                        {t.text}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
      {phaseAdd.toast}
      <BottomNav active="tasks" onNav={onNav} />
    </div>
  );
}

// The Category view: each category with tasks, folded until tapped, in the user's order. Drag ⠿ to move a
// category; "No category" stays last and has no handle.
function CategoryGroups({ groups, categories, sensors, openCats, setOpenCats, row, onMoveCategories }) {
  const movable = groups.filter(g => g.category.id !== null).map(g => g.category.id);
  const toggle = (id) => setOpenCats(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onMoveCategories(moveCategory(categories, movable, active.id, over.id));
  };
  const group = ({ category, tasks }, drag) => {
    const key = category.id ?? "none";
    const open = openCats.has(key);
    return (
      <div style={{ marginBottom: open ? 12 : 0 }}>
        <div style={{ display: "flex", alignItems: "center", borderBottom: `2px solid ${C.border}`, background: drag?.isDragging ? C.surface : "transparent" }}>
          <button onClick={() => toggle(key)} aria-expanded={open}
            style={{ flex: 1, minWidth: 0, minHeight: 48, display: "flex", alignItems: "center", gap: 10, padding: "8px 0", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left", color: C.text }}>
            {category.icon && <span aria-hidden="true" style={{ fontSize: 16 }}>{category.icon}</span>}
            <span style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 700 }}>{category.label}</span>
            <span style={{ fontSize: 13, color: C.textMid }}>{tasks.length} {tasks.length === 1 ? "task" : "tasks"} <span aria-hidden="true" style={{ fontSize: 11 }}>{open ? "▲" : "▼"}</span></span>
          </button>
          {drag ? <DragHandle label={category.label} {...drag} /> : <span style={{ width: 44, flexShrink: 0 }} />}
        </div>
        {open && tasks.map(t => row(t))}
      </div>
    );
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={movable} strategy={verticalListSortingStrategy}>
        {groups.map(g => g.category.id === null
          ? <div key="none">{group(g, null)}</div>
          : <Sortable key={g.category.id} id={g.category.id}>{(drag) => group(g, drag)}</Sortable>)}
      </SortableContext>
    </DndContext>
  );
}
